//@flow
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import ShiftImportHelper from 'common/entity/shiftMaster/ShiftImportHelper';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import camelCase from 'lodash/camelCase';

const getWorkingDaysAccessor = accessor =>
    ShiftImportHelper.getTimeSlotsAccessor({
        ...accessor,
        workingDaysAccessor: ['workingDays'],
        dayOfWeekIndex: 1
    }).pop();

const getBreakLocationAccessor = accessor =>
    ShiftImportHelper.getTimeSlotsAccessor({
        ...accessor,
        workingDaysAccessor: ['workingDays'],
        dayOfWeekIndex: 1
    });

const addWorkingDays = initialColumns => {
    const workingDaysColumns = [];

    const workingDays = Object.values(DayOfWeekType);
    const lastDay = workingDays.pop();
    workingDays.unshift(lastDay);

    workingDays.forEach(dayOfWeek => {
        const workingDayColumn = [
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.workingDays',
                id: `${dayOfWeek}.slot`,
                accessor: ['workingDays', dayOfWeek, 'slot'],
                templateValue: `masterImportTemplateValues.masterShift.${camelCase(
                    `workingDay-${dayOfWeek}`
                )}`,
                validateCell: ShiftImportHelper.validateWorkingDaysTimeSlot,
                serializeCell: ShiftImportHelper.serializeTimeSlot,
                getModelFieldAccessor: getWorkingDaysAccessor
            },
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.breaks',
                id: `${dayOfWeek}.breaks`,
                accessor: ['workingDays', dayOfWeek, 'breaks'],
                templateValue: `masterImportTemplateValues.masterShift.${camelCase(
                    `break-${dayOfWeek}`
                )}`,
                processCell: obj =>
                    ShiftImportHelper.processBreaks({ ...obj, dayOfWeek }),
                validateCell: ShiftImportHelper.validateWorkingDaysBreak,
                serializeCell: ShiftImportHelper.serializeBreakShift,
                getModelFieldAccessor: getBreakLocationAccessor
            },
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.dayStartLocation',
                id: `${dayOfWeek}.dayStartLocation`,
                accessor: [
                    'workingDays',
                    dayOfWeek,
                    'startLocation',
                    'address'
                ],
                templateValue:
                    'masterImportTemplateValues.masterShift.startLocation',
                getModelFieldAccessor: getBreakLocationAccessor
            },
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.dayEndLocation',
                id: `${dayOfWeek}.dayEndLocation`,
                accessor: ['workingDays', dayOfWeek, 'endLocation', 'address'],
                templateValue:
                    'masterImportTemplateValues.masterShift.endLocation',
                getModelFieldAccessor: getBreakLocationAccessor
            },
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.dayStartLat',
                id: `${dayOfWeek}.dayStartLat`,
                accessor: [
                    'workingDays',
                    dayOfWeek,
                    'startLocation',
                    'latLng',
                    'lat'
                ],
                templateValue:
                    'masterImportTemplateValues.masterShift.startLat',
                validateCell: ShiftImportHelper.validateWorkingDaysLocation,
                getModelFieldAccessor: getBreakLocationAccessor
            },
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.dayStartLng',
                id: `${dayOfWeek}.dayStartLng`,
                accessor: [
                    'workingDays',
                    dayOfWeek,
                    'startLocation',
                    'latLng',
                    'lng'
                ],
                templateValue:
                    'masterImportTemplateValues.masterShift.startLng',
                validateCell: ShiftImportHelper.validateWorkingDaysLocation,
                getModelFieldAccessor: getBreakLocationAccessor
            },
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.dayEndLat',
                id: `${dayOfWeek}.dayEndLat`,
                accessor: [
                    'workingDays',
                    dayOfWeek,
                    'endLocation',
                    'latLng',
                    'lat'
                ],
                templateValue: 'masterImportTemplateValues.masterShift.endLat',
                validateCell: ShiftImportHelper.validateWorkingDaysLocation,
                getModelFieldAccessor: getBreakLocationAccessor
            },
            {
                titleId: dayOfWeek,
                titleKey: 'masterShiftProperties.dayEndLng',
                id: `${dayOfWeek}.dayEndLng`,
                accessor: [
                    'workingDays',
                    dayOfWeek,
                    'endLocation',
                    'latLng',
                    'lng'
                ],
                templateValue: 'masterImportTemplateValues.masterShift.endLng',
                validateCell: ShiftImportHelper.validateWorkingDaysLocation,
                getModelFieldAccessor: getBreakLocationAccessor
            }
        ];
        workingDaysColumns.push(...workingDayColumn);
    });
    const columns = [...initialColumns, ...workingDaysColumns];
    return columns;
};

const SHIFT_IMPORT_FORMAT: Array<TableColumnTypes> = addWorkingDays([
    {
        titleKey: 'masterShiftProperties.id',
        id: 'id',
        disabled: true,
        fixed: true,
        required: true,
        templateValue: 'masterImportTemplateValues.masterShift.id'
    },
    {
        titleKey: 'masterShiftProperties.code',
        id: 'code',
        disabled: true,
        templateValue: 'masterImportTemplateValues.masterShift.code'
    },
    {
        titleKey: 'masterShiftProperties.name',
        id: 'name',
        required: true,
        templateValue: 'masterImportTemplateValues.masterShift.name'
    },
    {
        titleKey: 'masterShiftProperties.teamId',
        id: 'teamId',
        processCell: ShiftImportHelper.processTeam,
        validateCell: ShiftImportHelper.validateTeamId,
        templateValue: 'masterImportTemplateValues.masterShift.teamId',
        serializeCell: ShiftImportHelper.serializeTeam
    }
]);

export default SHIFT_IMPORT_FORMAT;
